import { type FunctionComponent, useRef, useState } from 'react';

import { useNavigate } from 'react-router';

import isEmail from 'validator/lib/isEmail.js';
import isStrongPassword from 'validator/lib/isStrongPassword.js';

import { Button, Input, Link } from '@dr.pogodin/react-utils';

// import SocialLoginBlock from 'components/SocialLoginBlock';
import Throbber from 'components/Throbber';

import { getFirebaseClientService } from 'services';

import { useGlobalState } from 'utils/global-state';

import IconEye from 'assets/images/icon-eye.svg';
import Logo from 'assets/images/logo-soul-sanctuary-new-150px.svg';

import buttonIconTheme from 'themes/Button/ai-app/password-toggle.scss';
import buttonThemeH48 from 'themes/Button/h48px.scss';
import inputTheme from 'themes/Input/ai-app/auth.scss';

import S from './style.scss';

const Register: FunctionComponent = () => {
  const usernameRef = useRef<HTMLInputElement>(null);
  const password1Ref = useRef<HTMLInputElement>(null);
  const password2Ref = useRef<HTMLInputElement>(null);
  const [username, setUsername] = useState('');
  const [password1, setPassword1] = useState('');
  const [password2, setPassword2] = useState('');

  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);

  const [showUsernameError, setShowUsernameError] = useState(false);
  const [showPassword1Error, setShowPassword1Error] = useState(false);
  const [showPassword2Error, setShowPassword2Error] = useState(false);
  const [registrationErrorMsg, setRegistrationErrorMsg] = useState('');
  const [inProgress, setInProgress] = useState(false);
  const [/* user */, setUser] = useGlobalState('user');

  const navigate = useNavigate();

  const usernameOk = isEmail(username);

  // NOTE: In Chrome auto-generated password suggestions may include no special
  // symbols, thus we won't require them to make it easier to create accounts.
  const password1Ok = isStrongPassword(password1, { minSymbols: 0 });

  const password2Ok = password2 === password1;

  if (usernameOk && showUsernameError) setShowUsernameError(false);
  if (password1Ok && showPassword1Error) setShowPassword1Error(false);
  if (password2Ok && showPassword2Error) setShowPassword2Error(false);

  const register = async () => {
    if (!usernameOk || !password1Ok || !password2Ok) {
      throw Error('Registration trigger with invalid initial state');
    }

    try {
      setInProgress(true);

      const { passesAppCheck, registerUser } = await getFirebaseClientService();

      const isHuman = await passesAppCheck();
      if (!isHuman) {
        throw Error('Failed to register at this time, try again later.');
      }

      const user = await registerUser(username, password1);
      setUser(user);
      setRegistrationErrorMsg('');
      void navigate('/');
    } catch (error: unknown) {
      // TODO: If error.code is "auth/email-already-in-use" we should add
      // the password reset link to the error message!
      const code = error && typeof error === 'object'
        && 'code' in error ? error.code : 'internal_error';
      setRegistrationErrorMsg(code as string);
    } finally {
      setInProgress(false);
    }
  };

  return (
    <div className={S.container}>
      <div className={S.content}>
        <Logo />
        <h1 styleName="title">Sign Up</h1>
        <div styleName="stretched">
          <div styleName="centeredText">
            Create your account to get started
          </div>
          <form autoComplete="on" styleName="registrationForm">
            <div styleName="inputWrapper">
              <Input
                autoComplete="email"
                id="username"
                name="username"
                onBlur={() => {
                  if (!usernameOk) setShowUsernameError(true);
                }}
                onChange={(e) => {
                  setUsername(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (!password1Ref.current) throw Error('Missing password 1 reference');
                    if (usernameOk) password1Ref.current.focus();
                    else setShowUsernameError(true);
                  }
                }}
                placeholder="Email Address"
                ref={usernameRef}
                theme={inputTheme}
                type="email"
                value={username}
              />
              {showUsernameError ? (
                <div styleName="error">
                  Not a valid email.
                </div>
              ) : null}
            </div>
            <div styleName="inputWrapper">
              <Input
                autoComplete="new-password"
                id="password"
                name="password"
                onBlur={() => {
                  if (!password1Ok) setShowPassword1Error(true);
                }}
                onChange={(e) => {
                  setPassword1(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (!password2Ref.current) throw Error('Missing password 2 reference');
                    if (password1Ok) password2Ref.current.focus();
                    else setShowPassword1Error(true);
                  }
                }}
                placeholder="Password"
                ref={password1Ref}
                theme={inputTheme}
                type={showPassword1 ? 'text' : 'password'}
                value={password1}
              />
              <Button
                onClick={() => {
                  setShowPassword1(!showPassword1);
                }}
                theme={buttonIconTheme}
              >
                <IconEye />
              </Button>
            </div>
            <div styleName="inputWrapper">
              <Input
                autoComplete="new-password"
                id="password2"
                name="password2"
                onBlur={() => {
                  if (!password2Ok) setShowPassword2Error(true);
                }}
                onChange={(e) => {
                  setPassword2(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (!password1Ref.current || !usernameRef.current) {
                      throw Error('Missing references');
                    }
                    if (!password1Ok) password1Ref.current.focus();
                    else if (!password2Ok) setShowPassword2Error(true);
                    else if (usernameOk) void register();
                    else usernameRef.current.focus();
                  }
                }}
                placeholder="Repeat Password"
                ref={password2Ref}
                theme={inputTheme}
                type={showPassword2 ? 'text' : 'password'}
                value={password2}
              />
              <Button
                onClick={() => {
                  setShowPassword2(!showPassword2);
                }}
                theme={buttonIconTheme}
              >
                <IconEye />
              </Button>
              {showPassword1Error ? (
                <div styleName="error">
                  The password is weak &mdash; use at least 8 symbols,
                  at least one lower and uppercase letter, at least one
                  number, at least one other character.
                </div>
              ) : null}
              {showPassword2Error ? (
                <div styleName="error">
                  Given password copies do not match.
                </div>
              ) : null}
              <div styleName="passwordRequirements">
                At least 8 symbols, must include lower and uppercase letters,
                digits, and punctuation characters.
              </div>
            </div>
            {inProgress ? <Throbber /> : (
              <Button
                disabled={!usernameOk || !password1Ok || !password2Ok}
                onClick={() => {
                  void register();
                }}
                theme={buttonThemeH48}
              >
                Create Account
              </Button>
            )}
            {registrationErrorMsg ? (
              <div styleName="error">
                {registrationErrorMsg}
              </div>
            ) : null}
          </form>
        </div>
        {/* <SocialLoginBlock /> */}
        <div styleName="haveAnAccountBlock">
          Have an account already? &zwnj;
          <Link styleName="link" to="/login">Log In</Link>
        </div>
      </div>
    </div>
  );
};

export default Register;
